import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { buildCategorizedGroups } from '@app/+authenticated/+timesheet/list/build-categorized-groups';
import {
  emptyCategorizedGroups,
  type CategorizedTimesheetGroups,
} from '@app/+authenticated/+timesheet/list/categorized-timesheet-row';
import { getCategorizedGroupsInputForRange } from '@app/+authenticated/+timesheet/list/timesheet-list-for-range.selectors';
import { type CategorizedGroupsInput } from '@app/+authenticated/+timesheet/list/timesheet-list.selectors';
import { hasRowOmittedFromPriority } from '@app/+authenticated/+timesheet/shared/priority/timesheet-priority.loader';
import type { PriorityTimesheet } from '@app/+authenticated/+timesheet/shared/priority/timesheet-priority.models';
import type { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';

import { payrollStepDefinitions } from './payroll-steps';
import { type PayrollRunScope, type PayrollStepId } from './payroll.model';

const firstStepId = payrollStepDefinitions[0].id;

/**
 * State of the payroll run in progress. A plain signals service rather than a signal store:
 * there is no backend, no entity collection and no request status. The progress panel renders
 * from the authenticated shell rather than as a child of the payroll page, which is why this
 * is shared state rather than component-local.
 *
 * Nothing here is persisted. Leaving the flow calls `reset()`, so returning asks for the
 * period again instead of resuming — and no payroll range is ever written to `page-filters`.
 */
@Injectable({ providedIn: 'root' })
export class PayrollRunService {
  #store = inject<Store<AppState>>(Store);

  /**
   * Identifies one run to analytics. Nothing here is persisted and no run id comes from the
   * backend, so without this the only way to group a run's events is session plus period — which
   * folds a manager's two runs of the same period into one.
   *
   * Minted when a run first gets a scope and kept until it is discarded, so it spans a mid-flow
   * re-scope. Progress resets there, but the manager is still inside the one preparation they
   * started — and the events that report a step reopening *because* of that re-scope only mean
   * anything as part of the same run as the completion they undo.
   */
  #runId = signal<string | null>(null);

  #scope = signal<PayrollRunScope | null>(null);
  #currentStepId = signal<PayrollStepId>(firstStepId);
  #skipped = signal<Set<PayrollStepId>>(new Set());

  /**
   * One signal rather than separate `loading` and `failed` booleans: the three public flags below
   * are all derived from it, so they cannot disagree, and a new call site cannot set one and
   * forget the other.
   *
   * Moved to 'loading' synchronously by `start()`/`changeScope()`, not by the loader that reacts
   * to the scope — `toObservable(scope)` emits via an effect, so a status only flipped inside
   * that pipe would still read 'loaded' for one tick after `start()` returns, during which
   * `timesheetGroups` reads a cold or previous-scope store. `timesheetsOutstanding`
   * (payroll-sources.ts) would report a false "0 outstanding" in that window.
   */
  #timesheetsStatus = signal<'idle' | 'loading' | 'loaded' | 'failed'>('idle');

  /** `null` while the API path is off, or after it failed and local categorization took over. */
  #priorityEntities = signal<Dictionary<PriorityTimesheet> | null>(null);

  /**
   * What the active step says a manager stands to lose by leaving it — unsaved payout hours in the
   * Minijob step's editor, the first step with anything to lose at all. Held here rather than read
   * off the step itself, because the three places a manager leaves a step from (the page's
   * Previous/Next/Skip, the panel's stepper, and `payrollExitGuard`) are all outside the step and
   * none of them knows which step is mounted, let alone what it holds.
   *
   * A plain field, not a signal: nothing renders or derives from it, only `canLeaveStep()` reads it
   * — and it is written from the step's constructor, which a reactive reader would turn into a
   * write during the very change detection that mounted the step.
   */
  #leaveGuard: (() => Promise<boolean>) | null = null;

  runId = this.#runId.asReadonly();
  scope = this.#scope.asReadonly();
  currentStepId = this.#currentStepId.asReadonly();
  skipped = this.#skipped.asReadonly();
  timesheetsLoading = computed(() => this.#timesheetsStatus() === 'loading');
  timesheetsLoadFailed = computed(() => this.#timesheetsStatus() === 'failed');

  /**
   * Anything but 'loaded'. A failed load leaves the store exactly as cold as before it started,
   * which `timesheetGroups` cannot tell apart from "genuinely nothing outstanding" — so
   * `usePayrollStepSources` omits the timesheets source rather than reporting a false all-clear,
   * the same way it does while a load is still in flight.
   */
  timesheetsUnavailable = computed(() => this.#timesheetsStatus() !== 'loaded');

  hasScope = computed(() => this.#scope() !== null);

  /**
   * The parameterised selector instance for the run's current scope, cached separately from
   * `timesheetGroups` below so a new `createSelector` chain is only built when the scope itself
   * changes. Reading `store.selectSignal(...)` directly inside `timesheetGroups` would make it
   * depend on the store's entire state signal (`selectSignal` is itself just
   * `computed(() => selector(state()))`), rebuilding a fresh, unmemoized selector instance on every
   * unrelated store dispatch while a run is open and discarding `createSelector`'s memoization each
   * time — `usePayrollStepSources`' `dayStatuses` already gets this right by building its selector
   * signals outside the per-read computed; this mirrors that.
   */
  #groupsInputSignal = computed<Signal<CategorizedGroupsInput> | null>(() => {
    const scope = this.#scope();

    if (!scope) {
      return null;
    }

    const minDate = toISODateString(scope.range.start);
    const maxDate = toISODateString(scope.range.end);

    return this.#store.selectSignal(getCategorizedGroupsInputForRange(minDate, maxDate, scope.departmentIds));
  });

  /**
   * The run's timesheets, categorized by severity, derived once here rather than separately by
   * every consumer (the timesheets step body and `usePayrollStepSources`' outstanding count) — both
   * previously built their own `getCategorizedTimesheetGroupsForRange` selector instance from the
   * same scope, computing the same O(timesheets x schedules) grouping twice on every store change.
   *
   * The priority map is applied here rather than inside the selector so a refreshed map does not
   * rebuild the selector chain.
   */
  timesheetGroups = computed<CategorizedTimesheetGroups>(() => {
    const input = this.#groupsInputSignal()?.();

    if (!input) {
      return emptyCategorizedGroups();
    }

    return buildCategorizedGroups({ ...input, priorityEntities: this.#priorityEntities() });
  });

  /** Loaded rows the endpoint did not answer for, whose conflicts must be fetched separately. */
  priorityOmitsLoadedRow = computed(() =>
    hasRowOmittedFromPriority(this.#groupsInputSignal()?.().timesheets ?? [], this.#priorityEntities()),
  );

  start(scope: PayrollRunScope): void {
    // A run that already has an id keeps it: `changeScope` routes through here, and the period
    // dialog's very first pick does too, so neither "the manager re-scoped" nor "the manager
    // finally chose a period" may mint a second id for one visit to the flow.
    this.#runId.update((runId) => runId ?? crypto.randomUUID());
    this.#scope.set(scope);
    this.#currentStepId.set(firstStepId);
    this.#skipped.set(new Set());
    this.#timesheetsStatus.set('loading');
    this.#priorityEntities.set(null);
  }

  /**
   * Reported by the loader in `payroll-timesheet-load.ts`, which owns the request — this service
   * holds the run's state and does not fetch. `null` leaves the rows categorized locally.
   */
  onPriorityLoaded(entities: Dictionary<PriorityTimesheet> | null): void {
    this.#priorityEntities.set(entities);
  }

  /**
   * Reported by `usePayrollScopeData`, which owns the request itself — this service holds the
   * run's state and does not fetch. Mirrors `PayrollBalancesService.onContractsSettled`, so both
   * step sources learn about their load the same way.
   *
   * Ignored unless a load is actually outstanding: a response from a run the manager has since
   * left, or from a scope that has already been replaced, must not announce that the *current*
   * scope's data has arrived.
   */
  onTimesheetsSettled(failed: boolean): void {
    if (this.#timesheetsStatus() !== 'loading') {
      return;
    }

    this.#timesheetsStatus.set(failed ? 'failed' : 'loaded');
  }

  /**
   * A scope change resets flow position and clears skips, because both are session state keyed
   * to the old scope. Work already completed is untouched — only derived progress resets.
   */
  changeScope(scope: PayrollRunScope): void {
    this.start(scope);
  }

  skip(id: PayrollStepId): void {
    this.#skipped.update((skipped) => new Set(skipped).add(id));
  }

  goTo(id: PayrollStepId): void {
    this.#currentStepId.set(id);
  }

  registerLeaveGuard(guard: () => Promise<boolean>): void {
    this.#leaveGuard = guard;
  }

  /**
   * Cleared by identity, never blindly: a step's destroy hook runs after the step replacing it has
   * already mounted and registered its own guard, so an unconditional clear would unregister the
   * incoming step's.
   */
  clearLeaveGuard(guard: () => Promise<boolean>): void {
    if (this.#leaveGuard === guard) {
      this.#leaveGuard = null;
    }
  }

  /** `true` when no step has anything to lose — which is every step but Minijob so far. */
  canLeaveStep(): Promise<boolean> {
    const guard = this.#leaveGuard;

    return guard ? guard() : Promise.resolve(true);
  }

  /**
   * The manager's own departure from the active step: asks it (`canLeaveStep`), and answers with
   * the step being left — or `null` when the manager keeps editing, or when the run has moved on
   * by itself while the question was open. The reconcile and auto-advance effects
   * (payroll-step-effects.ts) move the run unasked, and a step's answer can hold a dialog open
   * long enough for one of them to land; a "discard" given then is about the step the manager
   * was on, not the one they were carried to, so it moves and records nothing.
   */
  async confirmLeavingStep(): Promise<PayrollStepId | null> {
    const leaving = this.#currentStepId();
    const canLeave = await this.canLeaveStep();

    return canLeave && this.#currentStepId() === leaving ? leaving : null;
  }

  /**
   * `goTo` for a move the manager makes — Previous/Next on the page, a click in the panel's
   * stepper — so the active step is asked first. `goTo` itself is the unasked move the effects use.
   */
  async goToIfAllowed(id: PayrollStepId): Promise<boolean> {
    const leaving = await this.confirmLeavingStep();

    if (leaving === null) {
      return false;
    }

    this.goTo(id);

    return true;
  }

  reset(): void {
    this.#runId.set(null);
    this.#scope.set(null);
    this.#currentStepId.set(firstStepId);
    this.#skipped.set(new Set());
    this.#timesheetsStatus.set('idle');
    this.#priorityEntities.set(null);
    this.#leaveGuard = null;
  }
}
